/**
 * Absolute playback-state model + control reconciliation.
 *
 * The projected target position at any shared-clock instant is a pure function
 * of the last agreed control event. Receivers apply incoming controls with:
 *   - echo drop (own messages / recently-seen ids)
 *   - epoch ordering (discard stale, accept newer)
 *   - deterministic concurrent-edit tiebreak (same epoch, different content)
 * so late / reordered / duplicated delivery is harmless and both peers converge
 * without a server referee.
 */
import type { ControlAction, ControlMsg, PlaybackState } from "../shared/protocol.ts";
import { LIMITS } from "../shared/protocol.ts";

export function clampRate(rate: number): number {
  if (!Number.isFinite(rate)) return 1;
  return Math.min(LIMITS.MAX_RATE, Math.max(LIMITS.MIN_RATE, rate));
}

/** Coerce a non-finite number to a finite fallback (defensive input guard). */
function finite(v: number, fallback: number): number {
  return Number.isFinite(v) ? v : fallback;
}

/** Where the video *should be* at `nowSharedMs` given the current state. */
export function targetPositionS(state: PlaybackState, nowSharedMs: number): number {
  if (state.paused) return state.positionS;
  const elapsedS = (nowSharedMs - state.refSharedMs) / 1000;
  return state.positionS + elapsedS * state.rate;
}

/** Where a specific control event says the video should be at `nowSharedMs`. */
export function projectedFromControl(evt: ControlMsg, nowSharedMs: number): number {
  if (evt.paused) return evt.positionS;
  const elapsedS = (nowSharedMs - evt.atSharedMs) / 1000;
  return evt.positionS + elapsedS * evt.rate;
}

/**
 * Build the control message for a local user action. Bumps epoch by 1 over the
 * highest epoch seen so far. `positionS`/`atSharedMs` describe the action's
 * absolute anchor.
 */
export function makeControl(
  current: PlaybackState,
  action: ControlAction,
  opts: {
    positionS: number;
    atSharedMs: number;
    originId: string;
    msgId: string;
    paused: boolean;
    rate: number;
  },
): ControlMsg {
  return {
    t: "control",
    action,
    epoch: current.epoch + 1,
    originId: opts.originId,
    // Guard against non-finite inputs so a bad clock can't inject NaN, which
    // would make incomingWinsTie non-antisymmetric (both peers lose -> diverge).
    positionS: finite(opts.positionS, current.positionS),
    atSharedMs: finite(opts.atSharedMs, current.refSharedMs),
    rate: clampRate(opts.rate),
    paused: opts.paused,
    msgId: opts.msgId,
  };
}

function stateFromControl(evt: ControlMsg): PlaybackState {
  return {
    epoch: evt.epoch,
    positionS: evt.positionS,
    refSharedMs: evt.atSharedMs,
    paused: evt.paused,
    rate: clampRate(evt.rate),
    originId: evt.originId,
  };
}

export type ApplyResult =
  | { applied: true; state: PlaybackState }
  | { applied: false; reason: "echo" | "stale" | "tie-lost" | "idempotent"; state: PlaybackState };

/**
 * Deterministic winner between two concurrent controls at the SAME epoch.
 * Both peers compute this identically, so they converge. Rules, in order:
 *   1. pause beats play (safer to be paused together)
 *   2. otherwise the earlier atSharedMs wins (whoever acted first in shared time)
 *   3. otherwise lower originId wins (stable final tiebreak)
 * Returns true if `incoming` beats `currentOrigin`'s equivalent event.
 */
export function incomingWinsTie(incoming: ControlMsg, current: PlaybackState): boolean {
  const curPausePriority = current.paused ? 1 : 0;
  const inPausePriority = incoming.paused ? 1 : 0;
  if (inPausePriority !== curPausePriority) return inPausePriority > curPausePriority;
  if (incoming.atSharedMs !== current.refSharedMs) return incoming.atSharedMs < current.refSharedMs;
  return incoming.originId < current.originId;
}

/**
 * Apply an incoming control against current state.
 * @param selfId our clientId, for echo detection
 * @param recentMsgIds ids we have already originated/seen (echo ring buffer)
 */
export function applyControl(
  current: PlaybackState,
  incoming: ControlMsg,
  selfId: string,
  recentMsgIds: ReadonlySet<string>,
): ApplyResult {
  // Rule 1 — ignore our own echo (server relay may reflect to sender).
  if (incoming.originId === selfId || recentMsgIds.has(incoming.msgId)) {
    return { applied: false, reason: "echo", state: current };
  }
  // Rule 2 — epoch ordering.
  if (incoming.epoch < current.epoch) {
    return { applied: false, reason: "stale", state: current };
  }
  if (incoming.epoch > current.epoch) {
    return { applied: true, state: stateFromControl(incoming) };
  }
  // Equal epoch: a true no-op (same content AND same origin — e.g. a relayed
  // echo of the very state we hold) is idempotent. Identical content from a
  // DIFFERENT origin is a genuine concurrent clash and must go through the
  // deterministic tiebreak, so the server and both clients converge identically.
  if (
    incoming.originId === current.originId &&
    incoming.paused === current.paused &&
    incoming.positionS === current.positionS &&
    incoming.atSharedMs === current.refSharedMs &&
    clampRate(incoming.rate) === current.rate
  ) {
    return { applied: false, reason: "idempotent", state: current };
  }
  if (incomingWinsTie(incoming, current)) {
    return { applied: true, state: stateFromControl(incoming) };
  }
  return { applied: false, reason: "tie-lost", state: current };
}
